/**
 * LOAD TEST RESULTS — deliberately limited to the metrics that decide something.
 *
 * =====================================================================================
 * WHAT THIS PANEL SHOWS, AND WHY IT SHOWS ONLY THIS
 *
 * Five things:
 *   1. p50 and p99 per region, at a rate low enough that the front door cannot touch it.
 *   2. The duplicate-PIC count. Zero is the only acceptable value and it is the whole point.
 *   3. The gap ratio, per region — how many serials were burned per serial issued.
 *   4. app 429 vs gw 429, in SEPARATE columns that are never added together.
 *   5. p50/p99 per use case, so "what did UC4 measure" has an answer where it is asked.
 *
 * Everything else the reports contain — concurrency ladders, driver-headroom CPU tables,
 * per-cell features{} provenance, cross-region penalties at every percentile, the open-loop
 * arrival-rate experiment — is real and stays in `loadtest/results/*.md`, which is the artifact
 * of record. It was on this page and it made the page unreadable, which is a way of publishing
 * nothing.
 *
 * NO FIGURE HERE WAS TYPED BY HAND. Both summaries are derived at build time from the committed
 * markdown reports by `web/multi-region-summary.mjs` and `web/multi-instance-summary.mjs`, and
 * the build FAILS if a report's shape moves rather than emitting nulls — because a panel
 * rendering `n/a` over a real measured figure is a quiet lie and a zero would be a loud one.
 *
 * READ-ONLY, and that is a security decision rather than a missing feature. These APIs are
 * unauthenticated by design and this page is public, so a "run benchmark" button would be an
 * internet-reachable trigger that DoSes our own endpoints, spends EC2 money, and irreversibly
 * burns serials from a finite regulated field. A shared secret in the bundle would not fix it: a
 * secret shipped to a browser is a public string. The trigger is a script authorised by IAM.
 * =====================================================================================
 */

import { useEffect, useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Table from '@cloudscape-design/components/table';
import Badge from '@cloudscape-design/components/badge';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Spinner from '@cloudscape-design/components/spinner';
import {
  LATENCY_CLIENT_SIDE,
  RPS_LOWER_BOUND,
  TWO_KINDS_OF_429,
  RECLAMATION_OFF,
  WARM_FLEET_ONLY,
  NO_FAILURE_INJECTION,
} from '../lib/caveats.js';

const MR_CANDIDATES = [
  'benchmark/EC2-BENCHMARK-MULTI-REGION.json',
  'outputs/EC2-BENCHMARK-MULTI-REGION.json',
];
const MI_CANDIDATES = [
  'benchmark/EC2-BENCHMARK-MULTI-INSTANCE.json',
  'outputs/EC2-BENCHMARK-MULTI-INSTANCE.json',
];

const ms = (v) => (v === null || v === undefined ? 'n/a' : `${v} ms`);
const int = (v) => (v === null || v === undefined ? 'n/a' : Number(v).toLocaleString('en-US'));

/**
 * Which scenarios map to which use case, and what each one is for. `no-buffer` is included
 * because it is the deliberate failure case: it shows what the design costs WITHOUT the buffer,
 * and its app-429 count is the clearest evidence in the whole suite that the buffer is what
 * absorbs the burst rather than DynamoDB.
 */
const USE_CASES = [
  { scenario: 'steady', label: 'UC1 — serial vending', note: 'DynamoDB mark + warm Valkey buffer' },
  { scenario: 'uc2-relational', label: 'UC2 — Aurora PostgreSQL rules', note: 'adds a six-table SQL join and a ledger write' },
  { scenario: 'uc4-dsql', label: 'UC4 — Aurora DSQL rules', note: 'the same join, resolved by DSQL' },
  { scenario: 'no-buffer', label: 'no-buffer — the deliberate failure case', note: 'buffer removed, so every serial is a DynamoDB round trip' },
];

async function loadFirst(candidates, valid) {
  for (const path of candidates) {
    try {
      const res = await fetch(path, { cache: 'no-store' });
      if (!res.ok) continue;
      const json = await res.json();
      if (!valid(json)) continue;
      return json;
    } catch (_e) {
      /* try the next candidate */
    }
  }
  return null;
}

export default function LoadTestPanel() {
  const [state, setState] = useState({ loading: true, mr: null, mi: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [mr, mi] = await Promise.all([
        loadFirst(MR_CANDIDATES, (j) => j && Array.isArray(j.regions) && j.regions.length >= 2),
        loadFirst(MI_CANDIDATES, (j) => j && Array.isArray(j.comparisons) && j.comparisons.length > 0),
      ]);
      if (!cancelled) setState({ loading: false, mr, mi });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" /> <Box variant="p">Loading the committed load-test results…</Box>
      </Box>
    );
  }

  if (!state.mr && !state.mi) {
    return (
      <Alert type="warning" header="No load-test results are available in this build">
        <Box variant="p">
          Nothing is shown rather than zeros, because a zero would imply a measurement that did not
          happen. Run <Box variant="code">scripts/run-benchmark.sh --multi-region</Box> and republish
          the site to populate this panel.
        </Box>
      </Alert>
    );
  }

  return (
    <SpaceBetween size="l">
      {/* ------------------------------------------------ how to read any of this */}
      <Alert type="warning" header="Read this before quoting any figure here">
        <SpaceBetween size="xs">
          <Box variant="p" data-testid="lt-apigee">
            {LATENCY_CLIENT_SIDE}
          </Box>
          <Box variant="p" data-testid="lt-lower-bound">
            {RPS_LOWER_BOUND}
          </Box>
          <Box variant="p" data-testid="lt-warm">
            {WARM_FLEET_ONLY}
          </Box>
          <Box variant="p" data-testid="lt-no-failure">
            {NO_FAILURE_INJECTION}
          </Box>
        </SpaceBetween>
      </Alert>

      {state.mr && <PerRegion d={state.mr} />}
      {state.mi && <PerUseCase d={state.mi} />}
      {(state.mr || state.mi) && <Uniqueness mr={state.mr} mi={state.mi} />}
      {state.mr && <GapRatio d={state.mr} />}
      {(state.mr || state.mi) && <Throttles mr={state.mr} mi={state.mi} />}

      <Box variant="small" color="text-body-secondary">
        Derived at build time from{' '}
        <Box variant="code">loadtest/results/EC2-BENCHMARK-MULTI-REGION.md</Box> and{' '}
        <Box variant="code">loadtest/results/EC2-BENCHMARK-MULTI-INSTANCE.md</Box>, which remain the
        artifacts of record and carry the full ladders, driver-headroom evidence and per-cell
        provenance. No number on this page was typed by hand, and the build fails rather than
        rendering a partial figure if a report’s shape moves. Runs are triggered by{' '}
        <Box variant="code">scripts/run-benchmark.sh</Box>, authorised by IAM — deliberately not by a
        button on a public page.
      </Box>
    </SpaceBetween>
  );
}

/* ---------------------------------------------------------------- 1. p50/p99 per region */

function PerRegion({ d }) {
  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Measured by a load generator INSIDE EACH REGION at 25 rps — two orders of magnitude below either region's burst bucket, so the front door cannot touch these numbers. This is the SLA-relevant figure."
          actions={<Badge color="green">stored result</Badge>}
        >
          Latency per region
        </Header>
      }
    >
      <SpaceBetween size="m">
        <ColumnLayout columns={3} variant="text-grid">
          {d.regions.map((r) => (
            <div key={r.id}>
              <Box variant="awsui-key-label">{r.awsRegion}</Box>
              <Box variant="h1" fontSize="display-l" data-testid={`lt-p50-${r.id}`}>
                {ms(r.local.p50)}
              </Box>
              <Box variant="small" color="text-body-secondary">
                p50 · p99 <strong>{ms(r.local.p99)}</strong> · n={int(r.local.n)}
              </Box>
            </div>
          ))}
          <div>
            <Box variant="awsui-key-label">Divergence between the regions</Box>
            <Box variant="h1" fontSize="display-l" data-testid="lt-divergence">
              {ms(d.divergence.p50Ms)}
            </Box>
            <Box variant="small" color="text-body-secondary">
              at p50 · {ms(d.divergence.p99Ms)} at p99
            </Box>
          </div>
        </ColumnLayout>

        <Table
          variant="embedded"
          contentDensity="compact"
          data-testid="lt-region-table"
          columnDefinitions={[
            { id: 'r', header: 'Region', cell: (r) => <strong>{r.awsRegion}</strong> },
            { id: 'n', header: 'n', cell: (r) => int(r.local.n) },
            { id: 'p50', header: 'p50', cell: (r) => <strong>{ms(r.local.p50)}</strong> },
            { id: 'p95', header: 'p95', cell: (r) => ms(r.local.p95) },
            { id: 'p99', header: 'p99', cell: (r) => <strong>{ms(r.local.p99)}</strong> },
            { id: 'err', header: 'error %', cell: (r) => `${r.local.errorPct.toFixed(2)}%` },
          ]}
          items={d.regions}
          empty={<Box textAlign="center">No per-region latency data.</Box>}
        />

        <Box variant="p">
          <strong>The two regions measure as the same deployment, which is what a sound design
          predicts.</strong> Neither region’s hot path makes a cross-region call, so neither pays for
          the other’s existence — each serves its own callers out of its own buffer and its own
          DynamoDB table, with no replication between them.
        </Box>

        <Box variant="small" color="text-body-secondary">
          Measured {d.generatedAt || 'unknown'} at commit <Box variant="code">{d.gitSha || 'unknown'}</Box>{' '}
          from {d.regions.map((r) => `${r.instanceType} in ${r.az}`).join(' and ')}. Percentiles are
          nearest-rank (inclusive). This run exercises the UC1 serial path only and injects no
          failures, so it measures the two regions’ steady-state independence, not their failover.
        </Box>
      </SpaceBetween>
    </Container>
  );
}

/* ------------------------------------------------------------- 2. p50/p99 per use case */

function PerUseCase({ d }) {
  // Only the OFF-flag cells, because that is the deployed default and mixing flag states into one
  // row would average across a variable that deliberately moved.
  const rows = [];
  for (const uc of USE_CASES) {
    const cmp = (d.comparisons || []).find((c) => c.scenario === uc.scenario);
    if (!cmp) continue;
    const byRegion = new Map();
    for (const r of cmp.rows) {
      if (r.flag !== 'OFF') continue;
      if (!byRegion.has(r.awsRegion)) byRegion.set(r.awsRegion, []);
      byRegion.get(r.awsRegion).push(r);
    }
    for (const [awsRegion, cells] of byRegion) {
      const p50s = cells.map((c) => c.p50).filter((v) => v !== null);
      const p99s = cells.map((c) => c.p99).filter((v) => v !== null);
      rows.push({
        key: `${uc.scenario}-${awsRegion}`,
        label: uc.label,
        note: uc.note,
        awsRegion,
        runners: cells.length,
        // A RANGE across runners, not a mean. Two runners in two AZs driving the same allocator are
        // two samples of the same operating point, and averaging them would hide a spread that is
        // itself informative.
        p50: p50s.length ? `${Math.min(...p50s)} – ${Math.max(...p50s)} ms` : 'n/a',
        p99: p99s.length ? `${Math.min(...p99s)} – ${Math.max(...p99s)} ms` : 'n/a',
        app429: cells.reduce((a, c) => a + (c.app429 || 0), 0),
        gw429: cells.reduce((a, c) => a + (c.gw429 || 0), 0),
      });
    }
  }

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Each use case at 20–25 rps in both regions, from two runners per region in two AZs. The range spans the runners rather than averaging them."
        >
          Latency per use case
        </Header>
      }
    >
      <SpaceBetween size="m">
        <Table
          variant="embedded"
          contentDensity="compact"
          wrapLines
          data-testid="lt-usecase-table"
          trackBy="key"
          columnDefinitions={[
            {
              id: 'uc',
              header: 'Use case',
              cell: (r) => (
                <SpaceBetween size="xxs">
                  <Box fontWeight="bold">{r.label}</Box>
                  <Box variant="small" color="text-body-secondary">
                    {r.note}
                  </Box>
                </SpaceBetween>
              ),
              width: 300,
            },
            { id: 'region', header: 'Region', cell: (r) => r.awsRegion },
            { id: 'p50', header: 'p50 (across runners)', cell: (r) => <strong>{r.p50}</strong> },
            { id: 'p99', header: 'p99 (across runners)', cell: (r) => r.p99 },
            { id: 'app', header: 'app 429 (allocator)', cell: (r) => <strong>{int(r.app429)}</strong> },
            { id: 'gw', header: 'gw 429 (front door)', cell: (r) => int(r.gw429) },
          ]}
          items={rows}
          empty={<Box textAlign="center">No per-use-case data in this summary.</Box>}
        />

        <Alert type="info" header="Two things these rows must not be read as">
          <SpaceBetween size="xs">
            <Box variant="p">
              <strong>Not a UC4-versus-UC2 verdict.</strong> The two ran at different times against
              different connection states. UC4’s high p99 is Aurora DSQL connection establishment on
              a cold Lambda execution environment, not steady-state query cost — at 20 rps there are
              not enough requests to amortise it, and a production deployment would pool connections.
              Do not quote a UC4 p99 as an SLA figure.
            </Box>
            <Box variant="p">
              <strong>Not an argument against going relational.</strong> If the rules data has
              genuine foreign keys and needs one consistent view, simulating joins in application
              code over a document store is worse, not better — slower in aggregate and able to
              violate invariants a database would have enforced. The relational rows price the
              decision; they do not settle it.
            </Box>
          </SpaceBetween>
        </Alert>
      </SpaceBetween>
    </Container>
  );
}

/* ------------------------------------------------------------------- 3. zero duplicates */

function Uniqueness({ mr, mi }) {
  const u = (mr && mr.uniqueness) || {};
  const mu = (mi && mi.uniqueness) || {};
  const lw = (mi && mi.liveWindow) || {};
  const mrDup = u.totalDuplicates;
  const miDup = mu.totalDuplicates;
  const anyDup = (mrDup || 0) + (miDup || 0) > 0;

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Keyed on PICs, not serials. C01, C03 and C04 differ only in a routing element that is not part of the PIC, so they share a serial pool — and a serial-keyed check would pass while the tracking numbers collide."
        >
          Duplicate tracking numbers
        </Header>
      }
    >
      <SpaceBetween size="m">
        <ColumnLayout columns={3} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Tracking numbers issued and compared</Box>
            <Box variant="h1" fontSize="display-l" data-testid="lt-pic-total">
              {int((u.picsTotal || 0) + (mu.grandTotalPics || 0))}
            </Box>
            <Box variant="small" color="text-body-secondary">
              across both runs, both regions, every runner
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Duplicates found</Box>
            <Box
              variant="h1"
              fontSize="display-l"
              color={anyDup ? 'text-status-error' : 'text-status-success'}
              data-testid="lt-pic-duplicates"
            >
              {int((mrDup || 0) + (miDup || 0))}
            </Box>
            <Box variant="small" color="text-body-secondary">
              two-region run: {int(mrDup)} · multi-runner run: {int(miDup)}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Coordination between the regions</Box>
            <Box variant="h1" fontSize="display-l">
              none
            </Box>
            <Box variant="small" color="text-body-secondary">
              no replication, no Global Tables, no cross-region call
            </Box>
          </div>
        </ColumnLayout>

        <StatusIndicator type={anyDup ? 'error' : 'success'}>
          {anyDup
            ? 'DUPLICATE TRACKING NUMBERS OBSERVED — uniqueness is violated'
            : 'ZERO duplicate tracking numbers, issued concurrently from two regions with no coordination between them'}
        </StatusIndicator>

        {mi && (
          <Alert
            type={mu.windowsOverlapped ? 'success' : 'warning'}
            header={
              mu.windowsOverlapped
                ? 'The runners’ serial windows OVERLAPPED, so the zero is evidence rather than arithmetic'
                : 'At least one construct’s windows did NOT overlap, so that zero is arithmetic'
            }
          >
            <Box variant="p">
              Had the runners occupied non-overlapping stretches of the range, zero duplicates would
              be guaranteed by arithmetic and the check would prove nothing. Every cell was driven
              from an absolute shared epoch precisely so the windows would overlap and a collision
              would have been reachable.
            </Box>
          </Alert>
        )}

        {/*
          The reclamation live-window result. This is a LIVE caveat, not history: the assertion
          passed because there was nothing to examine, and a green tick without that qualifier
          would be exactly the false pass this run refuses to make.
        */}
        {lw.vacuous && !lw.breach && (
          <Alert
            type="warning"
            header="The 120-day no-reissue assertion passed VACUOUSLY, and that qualifier is the result"
            data-testid="lt-vacuous"
          >
            <SpaceBetween size="xs">
              <Box variant="p">
                The reclaimed free list was <strong>EMPTY</strong>. So with reclamation on, the
                allocator ran the free-list query on every refill, found nothing, and fell through to
                the high-water mark. No serial was reclaimed, therefore none could be reissued — the
                assertion holds, but it holds <strong>because there was nothing to examine</strong>,
                not because the rotation was demonstrated safe under load.
              </Box>
              <Box variant="p">{RECLAMATION_OFF}</Box>
              <Box variant="p">
                Where the rotation IS proven honestly:{' '}
                <Box variant="code">{lw.provenBy || 'services/serial-api/reclaim.test.js'}</Box>. No
                load test can substitute for it, because no load test runs for 128 days.
              </Box>
            </SpaceBetween>
          </Alert>
        )}
      </SpaceBetween>
    </Container>
  );
}

/* ------------------------------------------------------------------------ 4. gap ratio */

function GapRatio({ d }) {
  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Reported beside latency because a design can post an excellent p99 while burning many serials for each one it issues. Run-scoped: sampled from each region's own counters before and after, and differenced."
        >
          Gap ratio — serials burned per serial issued
        </Header>
      }
    >
      <SpaceBetween size="m">
        <Table
          variant="embedded"
          contentDensity="compact"
          data-testid="lt-gap-table"
          columnDefinitions={[
            { id: 'r', header: 'Region', cell: (r) => <strong>{r.awsRegion}</strong> },
            { id: 'a', header: 'allocated in the window', cell: (r) => int(r.gap.allocated) },
            { id: 'i', header: 'issued in the window', cell: (r) => int(r.gap.issued) },
            { id: 'g', header: 'gap', cell: (r) => int(r.gap.gap) },
            { id: 'p', header: 'gap ratio', cell: (r) => <strong>{r.gap.gapPercent}</strong> },
            { id: 'x', header: 'amplification', cell: (r) => `${r.gap.amplification}×` },
          ]}
          items={d.regions}
          empty={<Box textAlign="center">No gap data.</Box>}
        />

        <Box variant="p">
          <strong>A gap is the price of never issuing a duplicate.</strong> The high-water mark in
          DynamoDB advances <em>before</em> any serial from a block is vended, so losing a buffer
          costs a gap and never a collision. But every unissued serial in a discarded block is
          permanently burned out of a finite, regulated field — so this number belongs next to the
          latency, not in an appendix.
        </Box>

        <Box variant="small" color="text-body-secondary">
          Where a measurement window contains no refill, the ratio is reported as a{' '}
          <strong>missing measurement, never as zero waste</strong> — a design that burned ten
          serials per issued serial and one that burned none would both print 0% if absence were
          treated as zero. A lifetime ratio is dominated by whatever else has ever run against these
          deployments and must never be quoted as a property of the design.
        </Box>
      </SpaceBetween>
    </Container>
  );
}

/* ------------------------------------------------------------- 5. the two kinds of 429 */

function Throttles({ mr, mi }) {
  const rows = [];
  if (mr) {
    for (const r of mr.regions) {
      rows.push({
        key: `mr-${r.id}`,
        run: 'two-region, 25 rps',
        awsRegion: r.awsRegion,
        app429: r.local.app429,
        gw429: r.local.gw429,
        accountBurst: r.frontDoor.accountBurst,
      });
      rows.push({
        key: `mrc-${r.id}`,
        run: 'two-region, concurrent phase',
        awsRegion: r.awsRegion,
        app429: r.concurrent.app429,
        gw429: r.concurrent.gw429,
        accountBurst: r.frontDoor.accountBurst,
      });
    }
  }
  if (mi) {
    // The burst and no-buffer scenarios are where the allocator actually pushed back, so they are
    // the rows that carry information. Summed across runners WITHIN a region and a scenario only —
    // never across the app/gw split, and never across regions, because capacity does not aggregate.
    for (const scenario of ['burst', 'no-buffer']) {
      const cmp = (mi.comparisons || []).find((c) => c.scenario === scenario);
      if (!cmp) continue;
      const byRegion = new Map();
      for (const r of cmp.rows) {
        if (r.flag !== 'OFF') continue;
        const cur = byRegion.get(r.awsRegion) || { app: 0, gw: 0 };
        cur.app += r.app429 || 0;
        cur.gw += r.gw429 || 0;
        byRegion.set(r.awsRegion, cur);
      }
      for (const [awsRegion, v] of byRegion) {
        const reg = (mi.regions || []).find((x) => x.awsRegion === awsRegion);
        rows.push({
          key: `mi-${scenario}-${awsRegion}`,
          run: `multi-runner, ${scenario}`,
          awsRegion,
          app429: v.app,
          gw429: v.gw,
          accountBurst: reg ? reg.frontDoor.accountBurst : null,
        });
      }
    }
  }

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Two columns, never one. Only an app 429 is evidence about the allocator or the buffer; a bare 429 bounds only the offered load."
        >
          Throttles — the allocator versus the front door
        </Header>
      }
    >
      <SpaceBetween size="m">
        <Table
          variant="embedded"
          contentDensity="compact"
          data-testid="lt-throttle-table"
          trackBy="key"
          columnDefinitions={[
            { id: 'run', header: 'Run / scenario', cell: (r) => r.run },
            { id: 'region', header: 'Region', cell: (r) => <strong>{r.awsRegion}</strong> },
            {
              id: 'app',
              header: 'app 429 — the allocator refused',
              cell: (r) => <strong>{int(r.app429)}</strong>,
            },
            {
              id: 'gw',
              header: 'gw 429 — API Gateway refused',
              cell: (r) => int(r.gw429),
            },
            { id: 'burst', header: 'account burst in force', cell: (r) => int(r.accountBurst) },
          ]}
          items={rows}
          empty={<Box textAlign="center">No throttle data.</Box>}
        />

        <Alert type="info" header="Why these are never added together">
          <Box variant="p" data-testid="lt-429">
            {TWO_KINDS_OF_429}
          </Box>
        </Alert>

        <Alert type="warning" header="The buffer’s ceiling was TOUCHED, not measured">
          <SpaceBetween size="xs">
            <Box variant="p">
              Where an app 429 appears, the cause was the <strong>refill cycle</strong>, not the
              serial range and not a DynamoDB write limit. A refill is lock-serialised, so a pool has
              a refills-per-second ceiling entirely separate from how many numbers it has left — and
              that is what pushed back under burst. It is a tuning signal, not a capacity wall.
            </Box>
            <Box variant="p">
              <strong>The consequence is worth stating plainly: a pool can refuse requests while its
              utilisation looks perfectly healthy.</strong> The two failure modes have different time
              constants — running out of numbers takes months, running out of refill throughput takes
              seconds — and only the first is visible on the Capacity tab today.
            </Box>
            <Box variant="p">
              The <code>no-buffer</code> rows are the deliberate failure case and show what the
              design costs without the buffer: a p50 in the seconds and tens of thousands of
              allocator refusals. That is the buffer’s value, measured rather than asserted.
            </Box>
          </SpaceBetween>
        </Alert>

        <Box variant="small" color="text-body-secondary">
          <strong>
            The account burst in force when these runs were measured differed by more than 10×
            between the two regions, and that asymmetry has since been resolved.
          </strong>{' '}
          The <em>account burst in force</em> column above is read from each run’s own record, so it
          shows what actually applied to the figures beside it — not today’s quota. An AWS support
          case raised the smaller region’s bucket to match the larger one, and both regions were
          verified symmetric afterwards. That was pre-existing per-region account state which this
          project did not create — the two regions run identical code against identical resource
          shapes — so any burst-absorption gap between them was{' '}
          <strong>the front door, not the design</strong>, and the same would be true if it returned.
          Every run was capped below the lower region’s bucket so the comparison is of the design and
          not of two quotas, which is also why no burst-absorption maximum is reported for either
          region. <strong>No measured figure here was restated when the quota changed:</strong> a
          number measured under one limit does not become a number measured under another, and the
          cap stays in the tooling so later runs remain comparable with these.
        </Box>
      </SpaceBetween>
    </Container>
  );
}
